'use client'

import React, { useContext, useEffect, useRef } from 'react'
import * as S from './ImageComparison.styles'
import Icon from '../../atoms/Icon/Icon.component'
import { SectionThemeContext } from '../../../contexts/SectionTheme.context'
import { NormalizedImageComparisonProps } from '../../../models/organisms/ImageComparison'

function ImageComparison({
  sectionId,
  beforeImage,
  afterImage,
  beforeLabel = 'Before',
  afterLabel = 'After',
  initialPosition = 50,
}: NormalizedImageComparisonProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const sliderRef = useRef<HTMLDivElement>(null)
  const beforeWrapperRef = useRef<HTMLDivElement>(null)
  const draggableRef = useRef<Draggable | null>(null)
  const positionRef = useRef(initialPosition)
  const { selectedColorScheme } = useContext(SectionThemeContext)

  useEffect(() => {
    if (!containerRef.current || !sliderRef.current || !beforeWrapperRef.current) return

    let gsapInstance: typeof import('gsap').default
    let resizeObserver: ResizeObserver

    const initDraggable = async () => {
      gsapInstance = (await import('gsap')).default
      const Draggable = (await import('gsap/dist/Draggable')).default
      gsapInstance.registerPlugin(Draggable)

      const container = containerRef.current
      const slider = sliderRef.current
      const beforeWrapper = beforeWrapperRef.current
      if (!container || !slider || !beforeWrapper) return

      const setInitialPosition = () => {
        const containerWidth = container.offsetWidth
        const xPos = (initialPosition / 100) * containerWidth
        const clipPercent = 100 - initialPosition
        
        gsapInstance.set(slider, { x: xPos })
        gsapInstance.set(beforeWrapper, { clipPath: `inset(0 ${clipPercent}% 0 0)` })
        positionRef.current = initialPosition

        if (draggableRef.current) {
          draggableRef.current.applyBounds(container)
        }
      }

      setInitialPosition()

      const [draggable] = Draggable.create(slider, {
        type: 'x',
        bounds: container,
        edgeResistance: 1,
        zIndexBoost: false,
        onDrag: function () {
          const containerWidth = container.offsetWidth
          const percent = (this.x / containerWidth) * 100
          const clipPercent = 100 - percent
          gsapInstance.set(beforeWrapper, { clipPath: `inset(0 ${clipPercent}% 0 0)` })
          positionRef.current = percent
        },
      })

      draggableRef.current = draggable

      resizeObserver = new ResizeObserver(() => {
        const containerWidth = container.offsetWidth
        const xPos = (positionRef.current / 100) * containerWidth
        gsapInstance.set(slider, { x: xPos })
        draggable.applyBounds(container)
      })

      resizeObserver.observe(container)
    }

    initDraggable().catch(console.error)

    return () => {
      if (draggableRef.current) {
        draggableRef.current.kill()
      }
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [initialPosition])

  return (
    <S.ImageComparison ref={containerRef} id={sectionId}>
      <S.ImageWrapper>
        <S.Image src={afterImage.src} alt={afterLabel} />
        <S.ImageOverlay />
      </S.ImageWrapper>
      <S.BeforeImageWrapper ref={beforeWrapperRef}>
        <S.Image src={beforeImage.src} alt={beforeLabel} />
        <S.ImageOverlay />
      </S.BeforeImageWrapper>

      <S.SliderLine ref={sliderRef}>
        <svg className="SliderLineTop" width="69" height="36" viewBox="0 0 69 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M69 0H0C32.5 0 33.5 15.5 33.5 36C33.5 15.5 37 0 69 0Z"/>
        </svg>

        <svg className="SliderLineBottom" width="69" height="36" viewBox="0 0 69 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M69 36H0C32.5 36 33.5 20.5 33.5 0C33.5 20.5 37 36 69 36Z"/>
        </svg>

        <S.SliderHandle colorScheme={selectedColorScheme}>
          <Icon name="chevron-left" />
          <Icon name="chevron-right" />
        </S.SliderHandle>
      </S.SliderLine>

      <S.BeforeLabel>{beforeLabel}</S.BeforeLabel>
      <S.AfterLabel>{afterLabel}</S.AfterLabel>
    </S.ImageComparison>
  )
}

export default ImageComparison
